import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import classes from "../homePageNew.module.css";
import { AuthContext } from "../../../context/auth-context";
import useHomeModuleList from "../hooks/useHomeModuleList";
import useModuleSearch from "../hooks/useModuleSearch";
import ModuleListPane from "./ModuleListPane";
import ReportsModal from "./ReportsModal";
import ReportPreviewDrawer from "./ReportPreviewDrawer";

/**
 * The one shared panel behind every module tab — list + search + pagination
 * + reports, all module-agnostic. A module supplies only:
 *
 *   moduleKey    API module name ("discovery", "brandAudit", ...)
 *   moduleLabel  display name
 *   weightTag    the small subtitle beside the module name
 *   DetailPane   that module's own right-hand detail component
 *   buildFullViewUrl(record, { userId, customerId, combination }) — optional
 *   variant      "brand" (default) or "category" — passed straight through
 *                to RecordCard for Category Analysis's brandless list cards
 *
 * "View full <module>" navigates straight to that module's own real page
 * (fullViewUrl) rather than opening an in-place drawer — a side drawer for
 * this action read as confusing, per explicit product feedback.
 *
 * Everything that differs between modules lives in DetailPane; everything
 * that doesn't lives here once, so a new module tab is one small config
 * file rather than another copy of this wiring.
 */
export default function ModulePanel({
  moduleKey,
  moduleLabel,
  weightTag,
  DetailPane,
  buildFullViewUrl,
  variant = "brand",
  apiVersion = 1,
  includeCategory = true,
}) {
  const { user, selectedCustomerId } = useContext(AuthContext);
  const userId = user?.user_role?.user_id;
  const customerId = selectedCustomerId;
  const navigate = useNavigate();

  const list = useHomeModuleList({ moduleKey, userId, customerId, apiVersion, includeCategory });
  const search = useModuleSearch({ moduleKey, customerId, userId, apiVersion, includeCategory });

  const [selectedId, setSelectedId] = useState(null);
  const [reportsModalRecord, setReportsModalRecord] = useState(null);
  const [previewReport, setPreviewReport] = useState(null);
  // Which metrics combination the detail pane is showing — set by modules
  // that expose a combination selector (Brand Audit); null for single-
  // combination modules like Discovery.
  const [combination, setCombination] = useState(null);

  const activeRecords = search.isActive ? search.results : list.records;

  // Select the first record whenever the VISIBLE SET of records actually
  // changes — initial load, a page navigation, or a new set of search
  // results — so the detail pane is never stale/empty. Keyed on the
  // records' own ids (not list.records/search.results by reference) so a
  // same-page refresh (e.g. after retrying a failed run, which re-fetches
  // the same page and returns a new array with the same ids) doesn't steal
  // the selection away from whatever the user was just looking at.
  const activeRecordsKey = activeRecords.map((r) => r.transactionId).join(",");
  useEffect(() => {
    setSelectedId(activeRecords.length > 0 ? activeRecords[0].transactionId : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRecordsKey]);

  const selectedRecord = useMemo(
    () => activeRecords.find((r) => String(r.transactionId) === String(selectedId)) || null,
    [activeRecords, selectedId]
  );

  // A combination only means something within one record — reset it whenever
  // the selected record changes so a stale selection can't leak across.
  useEffect(() => {
    setCombination(null);
  }, [selectedId]);

  const fullViewUrl = useMemo(() => {
    if (!buildFullViewUrl || !selectedRecord || !userId) return null;
    return buildFullViewUrl(selectedRecord, { userId, customerId, combination });
  }, [buildFullViewUrl, selectedRecord, userId, customerId, combination]);

  const handleRetried = () => {
    if (search.isActive) {
      search.setPage(search.page);
    } else {
      list.refresh();
    }
  };

  // Only gates the very first load, before this module has ever shown
  // anything. Must never re-trigger once the user is searching — otherwise
  // the whole list pane (including the search input) unmounts on every
  // keystroke and the input loses focus/cursor position. Matches
  // DiscoveryV2/History/HistoryV2.js's own search: its loading state only
  // ever replaces the results table, never the search box around it.
  const showInitialLoading = !search.isActive && list.loading && list.records.length === 0;

  // True "this customer has zero runs for this module" — getHomePageData's
  // records array came back empty, not "a search matched nothing" (that
  // case keeps its own "No matches for ..." wording, in ModuleListPane).
  // Guarded to the non-search list so it can't flicker true while a search
  // is still in flight and its own totalCount hasn't arrived yet.
  const noDataAtAll = !search.isActive && !list.loading && list.totalCount === 0;

  return (
    <div className={classes.modSection}>
      <div className={classes.modSectionHead}>
        <div className={classes.modTitleBlock}>
          <span className={classes.modName}>{moduleLabel}</span>
          {weightTag && <span className={classes.weightTag}>{weightTag}</span>}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className={classes.metaStat}>
            <b>{list.totalCount}</b> total run{list.totalCount !== 1 ? "s" : ""}
          </span>
          {list.failedCount > 0 && (
            <>
              <span className={classes.metaStatSep}>·</span>
              <span className={`${classes.metaStat} ${classes.metaStatFail}`}>
                <b>{list.failedCount}</b> failed
              </span>
            </>
          )}
        </div>
      </div>

      {showInitialLoading ? (
        <div className={classes.loadingWrap}>
          <CircularProgress size={28} />
        </div>
      ) : (
        <div className={classes.mdSplit}>
          <ModuleListPane
            moduleLabel={moduleLabel}
            records={activeRecords}
            selectedId={selectedId}
            onSelect={(record) => setSelectedId(record.transactionId)}
            onRetried={handleRetried}
            query={search.query}
            onQueryChange={search.setQuery}
            loading={search.isActive ? search.loading : list.loading}
            page={search.isActive ? search.page : list.page}
            totalPages={search.isActive ? search.totalPages : list.totalPages}
            total={search.isActive ? search.totalCount : list.totalCount}
            limit={list.limit}
            onPageChange={search.isActive ? search.setPage : list.goToPage}
            totalHistoryCount={list.totalCount}
            onOpenHistory={() => {}}
            variant={variant}
            noDataAtAll={noDataAtAll}
          />
          <DetailPane
            record={selectedRecord}
            moduleLabel={moduleLabel}
            moduleKey={moduleKey}
            noDataAtAll={noDataAtAll}
            combination={combination}
            onCombinationChange={setCombination}
            onRetried={handleRetried}
            onViewFull={() => fullViewUrl && navigate(fullViewUrl)}
            fullViewUrl={fullViewUrl}
            userId={userId}
            onOpenReportsModal={setReportsModalRecord}
            onViewReport={setPreviewReport}
          />
        </div>
      )}

      <ReportsModal
        open={!!reportsModalRecord}
        onClose={() => setReportsModalRecord(null)}
        transactionId={reportsModalRecord?.transactionId}
        userId={userId}
        brand={reportsModalRecord?.brand}
        moduleKey={reportsModalRecord?.recordKind === "categoryAnalysis" ? "categoryAnalysis" : moduleKey}
        onViewReport={setPreviewReport}
      />
      <ReportPreviewDrawer
        open={!!previewReport}
        onClose={() => setPreviewReport(null)}
        jobId={previewReport?.jobId}
        title={previewReport?.category || "Report"}
      />
    </div>
  );
}
